import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type KeyboardEvent } from 'react';
import { classesQuery, templatesQuery, type GameClass } from '../../lib/templates';
import type { CreateRoomInput } from '../../lib/rooms';
import { useDialog } from '../../lib/useDialog';

interface NewRoomDialogProps {
  onClose: () => void;
  onSubmit: (input: CreateRoomInput) => void;
  pending: boolean;
}

/** Template select → class combobox with type-ahead → guest label → title (defaults server-side). */
export function NewRoomDialog({ onClose, onSubmit, pending }: NewRoomDialogProps) {
  const { data: templates } = useQuery(templatesQuery);
  const { data: classes } = useQuery(classesQuery);

  const [templateId, setTemplateId] = useState<number | null>(null);
  const [classQuery, setClassQuery] = useState('');
  const [classId, setClassId] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [guestLabel, setGuestLabel] = useState('');
  const [title, setTitle] = useState('');
  const { dialogRef, onBackdropClick } = useDialog(onClose);

  const selectedClass = classes?.find((gameClass) => gameClass.id === classId) ?? null;

  const filteredClasses = useMemo(() => {
    if (!classes) {
      return [];
    }
    const query = classQuery.trim().toLowerCase();
    const pool = query === '' ? classes : classes.filter((gameClass) => gameClass.name.toLowerCase().includes(query));
    return pool.slice(0, 8);
  }, [classes, classQuery]);

  const isListOpen = classQuery !== '' && classId === null && filteredClasses.length > 0 && !suggestionsDismissed;
  const activeOption = isListOpen && activeIndex >= 0 ? filteredClasses[activeIndex] : undefined;

  const canSubmit = templateId !== null && classId !== null && guestLabel.trim() !== '';

  function selectClass(gameClass: GameClass) {
    setClassId(gameClass.id);
    setClassQuery('');
    setActiveIndex(-1);
  }

  function onClassChange(value: string) {
    setClassId(null);
    setClassQuery(value);
    setActiveIndex(-1);
    setSuggestionsDismissed(false);
  }

  /** Standard combobox keyboard behavior: Arrow keys move the highlighted option, Enter commits it,
   * Escape dismisses just the suggestion list — `stopPropagation` keeps that Escape from also
   * closing the whole dialog via `useDialog`'s document-level listener. */
  function onClassKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!isListOpen) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % filteredClasses.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? filteredClasses.length - 1 : index - 1));
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0) {
        event.preventDefault();
        const gameClass = filteredClasses[activeIndex];
        if (gameClass) {
          selectClass(gameClass);
        }
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setSuggestionsDismissed(true);
    }
  }

  function submit() {
    if (!canSubmit || templateId === null || classId === null) {
      return;
    }
    const trimmedTitle = title.trim();
    onSubmit({
      templateId,
      classId,
      guestLabel: guestLabel.trim(),
      ...(trimmedTitle !== '' ? { title: trimmedTitle } : {}),
    });
  }

  return (
    <div
      ref={dialogRef}
      onClick={onBackdropClick}
      className="fixed inset-0 z-50 flex items-end justify-center bg-bg/80 p-4 md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="New room"
      tabIndex={-1}
    >
      <div className="w-full max-w-lg rounded-lg border border-border-strong bg-bg-elevated p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="display text-3xl">New room</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap min-w-tap rounded-md border border-border-strong px-3 text-sm text-text-muted hover:border-accent-border hover:text-text"
          >
            Close
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Template</span>
            <select
              value={templateId ?? ''}
              onChange={(event) => setTemplateId(event.target.value === '' ? null : Number(event.target.value))}
              className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text"
            >
              <option value="" disabled>
                Choose a template…
              </option>
              {(templates ?? []).filter((template) => template.published).map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} · {template.seriesLabel}
                </option>
              ))}
            </select>
          </label>

          <label className="relative flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Class</span>
            <input
              type="text"
              role="combobox"
              aria-expanded={isListOpen}
              aria-controls={isListOpen ? 'new-room-class-listbox' : undefined}
              aria-activedescendant={activeOption ? `new-room-class-option-${activeOption.id}` : undefined}
              aria-autocomplete="list"
              value={selectedClass ? selectedClass.name : classQuery}
              onChange={(event) => onClassChange(event.target.value)}
              onKeyDown={onClassKeyDown}
              placeholder="Type to search…"
              className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text"
            />
            {isListOpen && (
              <ul
                id="new-room-class-listbox"
                role="listbox"
                aria-label="Classes"
                className="absolute top-full z-10 mt-1 w-full rounded-md border border-border-strong bg-bg-elevated shadow-lg"
              >
                {filteredClasses.map((gameClass, index) => (
                  <li
                    key={gameClass.id}
                    id={`new-room-class-option-${gameClass.id}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    onClick={() => selectClass(gameClass)}
                    className={[
                      'flex min-h-tap w-full cursor-pointer items-center px-3 text-left text-sm text-text',
                      index === activeIndex ? 'bg-bg-elevated-hover' : 'hover:bg-bg-elevated-hover',
                    ].join(' ')}
                  >
                    {gameClass.name}
                  </li>
                ))}
              </ul>
            )}
          </label>

          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Guest label</span>
            <input
              type="text"
              value={guestLabel}
              onChange={(event) => setGuestLabel(event.target.value)}
              placeholder="e.g. Bishop main — Rein"
              className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Title (optional)</span>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={
                selectedClass && guestLabel ? `${selectedClass.name} — ${guestLabel}` : 'Defaults to "<Class> — <Guest>"'
              }
              className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text"
            />
          </label>
        </div>

        <button
          type="button"
          disabled={!canSubmit || pending}
          onClick={submit}
          className="mt-6 min-h-tap w-full rounded-md border border-accent-border bg-accent-dim px-4 text-base font-semibold text-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          Create room
        </button>
      </div>
    </div>
  );
}
